import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, CheckCircle, AlertCircle, ArrowRight, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Analytics } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_itpro-shop/artifacts/6nsrlqnk_Gemini_Generated_Image_wzvx61wzvx61wzvx.png";

export default function BusinessVerification() {
  const navigate = useNavigate();
  const { user, businessProfile, updateBusinessProfile, checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    company_name: "",
    user_role: "",
    company_size: "",
    industry: ""
  });

  // Track page view
  useEffect(() => {
    Analytics.verificationStarted();
  }, []);

  // Check if user already has a business profile
  useEffect(() => {
    const checkExistingProfile = async () => {
      try {
        const response = await axios.get(`${API}/business/profile`, {
          withCredentials: true
        });
        if (response.data) {
          // Profile exists, check if GST certificate is uploaded
          if (response.data.gst_certificate_uploaded) {
            // Fully verified, go to dashboard
            navigate("/dashboard", { replace: true });
          } else {
            // Profile exists but no license, go to step 2
            updateBusinessProfile(response.data);
            Analytics.verificationStep2Started();
            setStep(2);
          }
        }
      } catch (error) {
        // No profile exists, stay on step 1
        if (error.response?.status !== 404) {
          console.error("Error checking profile:", error);
        }
      } finally {
        setCheckingProfile(false);
      }
    };

    checkExistingProfile();
  }, [navigate, updateBusinessProfile]);

  // If already has business profile, redirect to dashboard
  useEffect(() => {
    if (businessProfile && businessProfile.gst_certificate_uploaded) {
      navigate("/dashboard", { replace: true });
    }
  }, [businessProfile, navigate]);

  const companySizes = [
    { value: "1-10", label: "1-10 employees" },
    { value: "11-50", label: "11-50 employees" },
    { value: "51-200", label: "51-200 employees" },
    { value: "201-500", label: "201-500 employees" },
    { value: "500+", label: "500+ employees" }
  ];

  const industries = [
    { value: "technology", label: "Technology" },
    { value: "healthcare", label: "Healthcare" },
    { value: "finance", label: "Finance & Banking" },
    { value: "education", label: "Education" },
    { value: "retail", label: "Retail & E-commerce" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "professional_services", label: "Professional Services" },
    { value: "other", label: "Other" }
  ];

  const roles = [
    { value: "owner", label: "Business Owner / CEO" },
    { value: "it_manager", label: "IT Manager / Director" },
    { value: "procurement", label: "Procurement Manager" },
    { value: "cfo", label: "CFO / Finance Director" },
    { value: "operations", label: "Operations Manager" },
    { value: "other", label: "Other" }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProfileSubmit = async () => {
    if (!formData.company_name || !formData.user_role || !formData.company_size) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/business/profile`, formData, {
        withCredentials: true
      });
      updateBusinessProfile(response.data.profile);
      Analytics.verificationStep1Completed(formData.company_size);
      toast.success("Business profile created!");
      Analytics.verificationStep2Started();
      setStep(2);
    } catch (error) {
      // If profile already exists, fetch it and proceed to step 2
      if (error.response?.status === 400 && error.response?.data?.detail?.includes("already exists")) {
        try {
          const profileResponse = await axios.get(`${API}/business/profile`, {
            withCredentials: true
          });
          if (profileResponse.data) {
            updateBusinessProfile(profileResponse.data);
            if (profileResponse.data.gst_certificate_uploaded) {
              navigate("/dashboard", { replace: true });
            } else {
              toast.info("Profile already exists. Please upload your GST certificate.");
              Analytics.verificationStep2Started();
              setStep(2);
            }
          }
        } catch (fetchError) {
          toast.error("Failed to load existing profile");
        }
      } else {
        toast.error(error.response?.data?.detail || "Failed to create profile");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ["application/pdf", "image/jpeg", "image/png", "image/jpg"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload PDF, JPEG, or PNG");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }
    setUploadedFile(file);
  };

  const handleLicenseUpload = async () => {
    if (!uploadedFile) {
      toast.error("Please upload your GST certificate");
      return;
    }

    setLoading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", uploadedFile);
      
      await axios.post(`${API}/business/upload-license`, formDataUpload, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      Analytics.verificationCompleted();
      toast.success("Trade license uploaded! Your account is being verified.");
      navigate("/configure");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload license");
    } finally {
      setLoading(false);
    }
  };

  const skipLicenseUpload = () => {
    Analytics.verificationSkipped();
    toast.info("You can upload your GST certificate later from settings");
    navigate("/configure");
  };

  // Show loading while checking for existing profile
  if (checkingProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src={LOGO_URL} alt="OneIT" className="w-14 h-14 object-contain" />
          <span className="font-heading font-bold text-2xl">OneIT</span>
        </div>

        {/* Progress indicator */}
        <div className="flex items-center gap-4 mb-8 justify-center">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 1 ? "bg-[#007AFF] text-foreground" : "bg-secondary text-muted-foreground"
            }`}>
              {step > 1 ? <CheckCircle className="w-4 h-4" /> : "1"}
            </div>
            <span className="text-sm hidden sm:block">Business Details</span>
          </div>
          <div className="w-12 h-px bg-border" />
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= 2 ? "bg-[#007AFF] text-foreground" : "bg-secondary text-muted-foreground"
            }`}>
              2
            </div>
            <span className="text-sm hidden sm:block">GST Certificate</span>
          </div>
        </div>

        {/* Welcome message */}
        {user && (
          <div className="text-center mb-8">
            <p className="text-muted-foreground">
              Welcome, <span className="text-foreground font-medium">{user.name}</span>
            </p>
          </div>
        )}

        {/* Step 1: Business Details */}
        {step === 1 && (
          <div className="bg-card border border-border rounded-xl p-8 animate-slide-up">
            <h2 className="font-heading text-2xl font-semibold mb-2">Complete Your Business Profile</h2>
            <p className="text-muted-foreground mb-8">Tell us about your business to get personalized solutions</p>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  placeholder="Enter your company name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange("company_name", e.target.value)}
                  data-testid="company-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user_role">Your Role *</Label>
                <Select
                  value={formData.user_role}
                  onValueChange={(value) => handleInputChange("user_role", value)}
                >
                  <SelectTrigger id="user_role" data-testid="user-role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_size">Company Size *</Label>
                <Select
                  value={formData.company_size}
                  onValueChange={(value) => handleInputChange("company_size", value)}
                >
                  <SelectTrigger id="company_size" data-testid="company-size-select">
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    {companySizes.map(size => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Select
                  value={formData.industry}
                  onValueChange={(value) => handleInputChange("industry", value)}
                >
                  <SelectTrigger id="industry" data-testid="industry-select">
                    <SelectValue placeholder="Select industry (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(ind => (
                      <SelectItem key={ind.value} value={ind.value}>
                        {ind.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleProfileSubmit}
                disabled={loading}
                className="w-full bg-white text-black hover:bg-gray-200 h-12"
                data-testid="continue-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: GST Certificate Upload */}
        {step === 2 && (
          <div className="bg-card border border-border rounded-xl p-8 animate-slide-up">
            <h2 className="font-heading text-2xl font-semibold mb-2">Upload GST Certificate</h2>
            <p className="text-muted-foreground mb-8">
              Upload your GST certificate to complete business verification
            </p>

            <div className="space-y-6">
              <div
                className={`upload-zone rounded-xl p-8 text-center cursor-pointer ${dragOver ? "dragover" : ""}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-upload").click()}
                data-testid="upload-zone"
              >
                <input
                  id="file-upload"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={handleFileSelect}
                  data-testid="file-input"
                />
                
                {uploadedFile ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto bg-[#00FF88]/10 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-8 h-8 text-[#00FF88]" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{uploadedFile.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFile(null);
                      }}
                      data-testid="remove-file-btn"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 mx-auto bg-secondary rounded-full flex items-center justify-center">
                      <Upload className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">Choose files to Upload</p>
                      <p className="text-sm text-muted-foreground">or drag and drop them here</p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Supported: PDF, JPEG, PNG (Max 10MB)
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 p-4 rounded-lg bg-[#007AFF]/10 border border-[#007AFF]/20">
                <AlertCircle className="w-5 h-5 text-[#007AFF] shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-foreground">Why do we need this?</p>
                  <p className="text-muted-foreground mt-1">
                    Your GST certificate helps us verify your business and provide you with 
                    exclusive business pricing and solutions.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  variant="outline"
                  onClick={skipLicenseUpload}
                  className="flex-1 border-[#007AFF]/20 hover:bg-white/5"
                  data-testid="skip-btn"
                >
                  Skip for Now
                </Button>
                <Button
                  onClick={handleLicenseUpload}
                  disabled={loading || !uploadedFile}
                  className="flex-1 bg-white text-black hover:bg-gray-200"
                  data-testid="complete-verification-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      Complete Verification
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
